import type { FilterOperator } from '@/types/api'

export type FilterEditorType = 'enum' | 'text' | 'number' | 'date' | 'boolean'

export interface OperatorOption {
  value: FilterOperator
  label: string
}

export const ENUM_OPERATORS: OperatorOption[] = [
  { value: 'in', label: 'Is Any Of' },
  { value: 'notIn', label: 'Is None Of' },
]

export const TEXT_OPERATORS: OperatorOption[] = [
  { value: 'contains', label: 'Contains' },
  { value: 'eq', label: 'Equals' },
  { value: 'neq', label: 'Not Equals' },
  { value: 'startsWith', label: 'Starts With' },
  { value: 'endsWith', label: 'Ends With' },
  { value: 'notContains', label: 'Not Contains' },
]

export const NUMBER_OPERATORS: OperatorOption[] = [
  { value: 'eq', label: '=' },
  { value: 'neq', label: '≠' },
  { value: 'gt', label: '>' },
  { value: 'lt', label: '<' },
  { value: 'gte', label: '≥' },
  { value: 'lte', label: '≤' },
]

export const DATE_OPERATORS: OperatorOption[] = [
  { value: 'eq', label: 'Is' },
  { value: 'gt', label: 'Is After' },
  { value: 'lt', label: 'Is Before' },
  { value: 'gte', label: 'Is On Or After' },
  { value: 'lte', label: 'Is On Or Before' },
]

export function getFilterEditorType(
  dataType: string | undefined,
  hasOptions: boolean
): FilterEditorType {
  if (hasOptions) return 'enum'
  switch (dataType?.toLowerCase()) {
    case 'boolean':
      return 'boolean'
    case 'number':
    case 'integer':
    case 'currency':
    case 'percentage':
      return 'number'
    case 'date':
    case 'datetime':
      return 'date'
    default:
      return 'text'
  }
}

export function getDefaultOperator(
  editorType: FilterEditorType
): FilterOperator {
  switch (editorType) {
    case 'enum':
      return 'in'
    case 'text':
      return 'contains'
    case 'number':
      return 'eq'
    case 'date':
      return 'eq'
    case 'boolean':
      return 'eq'
  }
}

export function formatFilterDisplayValue(
  value: unknown,
  _operator: FilterOperator,
  editorType: FilterEditorType
): string {
  if (value === null || value === undefined) return ''

  if (editorType === 'enum' && Array.isArray(value)) {
    if (value.length === 0) return ''
    if (value.length <= 2) return value.join(' + ')
    return `${value.slice(0, 2).join(' + ')} +${value.length - 2}`
  }

  if (editorType === 'boolean') {
    return value === true || value === 'true' ? 'True' : 'False'
  }

  return String(value)
}

export function getOperatorLabel(
  operator: FilterOperator,
  editorType: FilterEditorType
): string {
  let operators: OperatorOption[]
  switch (editorType) {
    case 'enum':
      operators = ENUM_OPERATORS
      break
    case 'text':
      operators = TEXT_OPERATORS
      break
    case 'number':
      operators = NUMBER_OPERATORS
      break
    case 'date':
      operators = DATE_OPERATORS
      break
    default:
      return ''
  }
  return operators.find((op) => op.value === operator)?.label ?? operator
}
